import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import api from '../lib/api';
import useAsync from '../hooks/useAsync';
import Loader, { ErrorState } from '../components/Loader';
import { formatINR } from '../lib/whatsapp';

const blank = { title: '', price: '', description: '', active: true };

export default function AdminAddons() {
  const { data: items, loading, error, reload } = useAsync(
    () => api.get('/addons?all=true').then((r) => r.data),
    []
  );

  const [editing, setEditing] = useState(null); // addon _id | 'new' | null
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(blank); setEditing('new'); };
  const openEdit = (a) => {
    setForm({ title: a.title, price: a.price, description: a.description || '', active: a.active });
    setEditing(a._id);
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: Number(form.price) || 0 };
    try {
      if (editing === 'new') await api.post('/addons', payload);
      else await api.put(`/addons/${editing}`, payload);
      close();
      reload();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this add-on?')) return;
    await api.delete(`/addons/${id}`);
    reload();
  };

  if (loading) return <Loader label="Loading add-ons…" />;
  if (error) return <ErrorState message="Couldn't load add-ons." onRetry={reload} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Add-ons</h1>
          <p className="text-sm text-ink-400">
            {items.length} services — these power “Enhance with add-ons” on the website & app.
          </p>
        </div>
        <button onClick={openNew} className="btn-primary !py-2.5 text-sm">
          <Plus size={16} /> Add Service
        </button>
      </div>

      <div className="glass overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-ink-400">
            <tr>
              <th className="px-5 py-4">Service</th>
              <th className="px-5 py-4">Price</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((a) => (
              <tr key={a._id} className="transition hover:bg-white/[0.03]">
                <td className="px-5 py-4">
                  <p className="font-medium text-white">{a.title}</p>
                  {a.description ? <p className="text-xs text-ink-400">{a.description}</p> : null}
                </td>
                <td className="px-5 py-4 font-semibold text-gradient">{formatINR(a.price)}</td>
                <td className="px-5 py-4">
                  <span className={`rounded-pill px-2.5 py-1 text-xs font-medium ${a.active ? 'bg-success/15 text-success' : 'bg-white/5 text-ink-400'}`}>
                    {a.active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(a)} className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-ink-200 hover:bg-white/5">
                      <Pencil size={13} /> Edit
                    </button>
                    <button onClick={() => remove(a._id)} className="flex items-center gap-1 rounded-lg border border-danger/30 px-3 py-1.5 text-xs text-danger hover:bg-danger/10">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="py-12 text-center text-ink-400">No add-ons yet. Add your first service.</p>}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur"
            onClick={close}
          >
            <motion.form
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={save}
              className="glass w-full max-w-md space-y-4 p-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold">{editing === 'new' ? 'Add Service' : 'Edit Service'}</h2>
                <button type="button" onClick={close}><X size={20} className="text-ink-400" /></button>
              </div>

              <label className="block">
                <span className="mb-1 block text-sm text-ink-300">Title</span>
                <input
                  type="text" value={form.title} required
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-sm text-ink-300">Price (₹)</span>
                <input
                  type="number" value={form.price} required min="0"
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-sm text-ink-300">Description (optional)</span>
                <textarea
                  rows={2} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none focus:border-brand"
                />
              </label>

              <label className="flex items-center gap-2 text-sm text-ink-200">
                <input
                  type="checkbox" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="h-4 w-4 accent-brand"
                />
                Active (visible to customers)
              </label>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={close} className="btn-ghost flex-1">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
